import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Loader2, AlertCircle, Wrench, Calendar, User, Flag, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface MaintenanceRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed';
  requestedBy: string;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  plumbing: 'Plomería',
  electrical: 'Eléctrico',
  structural: 'Estructural',
  cleaning: 'Limpieza',
  other: 'Otro',
};

const priorityColors: Record<string, string> = {
  low: 'text-slate-400',
  medium: 'text-amber-400',
  high: 'text-red-400',
};

const priorityLabels: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
};

const statusLabels: Record<string, string> = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  completed: 'Completado',
};

const emptyForm = {
  title: '',
  description: '',
  category: 'other',
  priority: 'medium' as MaintenanceRequest['priority'],
};

export default function Maintenance() {
  const { user } = useAuth();
  const condoId = user?.condoId || 'condo-default';
  const [requests, setRequests] = useState<MaintenanceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/maintenance/condo/${condoId}`);
      setRequests(Array.isArray(res.data) ? res.data : res.data?.requests || []);
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        setError(err?.response?.data?.message || 'Error al cargar solicitudes de mantenimiento');
      }
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/maintenance', { ...form, condoId, requestedBy: user?.id || user?.email });
      setForm(emptyForm);
      setShowForm(false);
      loadRequests();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al crear solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/maintenance/${id}`);
      setRequests(prev => prev.filter(r => r.id !== id));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al eliminar');
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)] text-[var(--v-text-primary)] p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Wrench className="w-7 h-7 text-[var(--v-teal-400)]" />
            <h1 className="text-2xl font-bold">Mantenimiento</h1>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-[var(--v-teal-400)] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[var(--v-teal-500)] transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nueva solicitud
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-[var(--v-danger-bg)] border border-[var(--v-danger)]/30 rounded-lg p-3 flex items-center gap-2 text-[var(--v-danger)]">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 bg-[var(--v-surface-1)] rounded-xl p-4 space-y-4 border border-[var(--v-border-light)]">
            <div>
              <label className="block text-sm text-[var(--v-text-secondary)] mb-1">Título</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full bg-[var(--v-surface-2)] rounded-lg px-3 py-2 text-[var(--v-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] border border-[var(--v-border-light)]"
                placeholder="Describa el problema"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--v-text-secondary)] mb-1">Descripción</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                className="w-full bg-[var(--v-surface-2)] rounded-lg px-3 py-2 text-[var(--v-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] border border-[var(--v-border-light)]"
                rows={3}
                placeholder="Detalles adicionales"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[var(--v-text-secondary)] mb-1">Categoría</label>
                <select
                  value={form.category}
                  onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-[var(--v-surface-2)] rounded-lg px-3 py-2 text-[var(--v-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] border border-[var(--v-border-light)]"
                >
                  {Object.entries(categoryLabels).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[var(--v-text-secondary)] mb-1">Prioridad</label>
                <select
                  value={form.priority}
                  onChange={e => setForm({ ...form, priority: e.target.value as MaintenanceRequest['priority'] })}
                  className="w-full bg-[var(--v-surface-2)] rounded-lg px-3 py-2 text-[var(--v-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--v-teal-400)] border border-[var(--v-border-light)]"
                >
                  {Object.entries(priorityLabels).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="bg-[var(--v-teal-400)] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[var(--v-teal-500)] disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Enviando...' : 'Crear solicitud'}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setForm(emptyForm); }}
                className="bg-[var(--v-surface-2)] px-4 py-2 rounded-lg hover:bg-[var(--v-surface-3)] text-[var(--v-text-primary)] transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-400)]" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-[var(--v-text-tertiary)]">
            <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay solicitudes de mantenimiento</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <div key={req.id} className="bg-[var(--v-surface-1)] rounded-xl p-4 flex items-start justify-between gap-4 border border-[var(--v-border-light)]">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold truncate text-white">{req.title}</h3>
                    <span className={`text-xs ${priorityColors[req.priority] || ''}`}>
                      <Flag className="w-3 h-3 inline" /> {priorityLabels[req.priority] || req.priority}
                    </span>
                  </div>
                  {req.description && <p className="text-sm text-[var(--v-text-secondary)] mb-2">{req.description}</p>}
                  <div className="flex flex-wrap gap-3 text-xs text-[var(--v-text-tertiary)]">
                    <span className="bg-[var(--v-surface-2)] px-2 py-0.5 rounded">{categoryLabels[req.category] || req.category}</span>
                    <span>{statusLabels[req.status] || req.status}</span>
                    {req.createdAt && <span><Calendar className="w-3 h-3 inline" /> {new Date(req.createdAt).toLocaleDateString()}</span>}
                    {req.requestedBy && <span><User className="w-3 h-3 inline" /> {req.requestedBy}</span>}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(req.id)}
                  className="text-[var(--v-text-tertiary)] hover:text-[var(--v-danger)] flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
